import { PublicKey } from '@solana/web3.js';

import { proxyAxios } from 'api/apiBase/axios';
import type { KLine } from 'types/charts';

const SOLANA_RPC_URL = 'https://api.mainnet-beta.solana.com';
const TOKEN_PROGRAM_IDS = new Set([
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
]);

export interface SolanaMint {
  address: string;
  decimals: number;
  symbol: string;
}

export interface SolanaAccount {
  data: [string, 'base64'];
  executable: boolean;
  lamports: number;
  owner: string;
}

export interface SolanaPoolAccountProfile {
  accountSize: number;
  discriminatorHex?: string;
  layout: {
    mintA: number;
    mintB: number;
    vaultA: number;
    vaultB: number;
  };
  mintA: SolanaMint;
  mintB: SolanaMint;
  programId: string;
  vaultA: string;
  vaultB: string;
}

export interface DecodedSolanaPoolAccount {
  accountSize: number;
  mintA: string;
  mintB: string;
  owner: string;
  vaultA: string;
  vaultB: string;
}

export interface SolanaTokenAccountSnapshot {
  address: string;
  amountRaw: string;
  decimals: number;
  mint: string;
}

export interface SolanaMintAccountSnapshot {
  address: string;
  decimals: number;
  program: string;
}

export interface SolanaPublicPoolSnapshot {
  accountSize: number;
  feeRate?: number;
  mintA: SolanaMint;
  mintB: SolanaMint;
  owner: string;
  poolId: string;
  price?: number;
  slot: number;
  tvlUsd?: number;
  vaultA: SolanaTokenAccountSnapshot;
  vaultB: SolanaTokenAccountSnapshot;
  vaultSlot: number;
  volume24hUsd?: number;
}

interface SolanaRpcResponse<T> {
  error?: {
    code: number;
    message: string;
  };
  result?: T;
}

const bytesToHex = (bytes: Uint8Array) =>
  Array.from(bytes)
    .map(value => value.toString(16).padStart(2, '0'))
    .join('');

export const decodeBase64 = (value: string) =>
  Uint8Array.from(atob(value), character => character.charCodeAt(0));

export const readPublicKey = (bytes: Uint8Array, offset: number) => {
  if (offset < 0 || offset + 32 > bytes.length) {
    throw new Error('Solana pool layout is outside account data');
  }
  return new PublicKey(bytes.slice(offset, offset + 32)).toBase58();
};

export const ensureFiniteNumber = (value: unknown): number | undefined => {
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
};

export const decodeSolanaPoolAccount = (
  profile: SolanaPoolAccountProfile,
  account: SolanaAccount,
  label: string,
  vaultNoun = 'vault',
): DecodedSolanaPoolAccount => {
  if (account.owner !== profile.programId) {
    throw new Error(`${label} program owner mismatch`);
  }
  if (account.executable) {
    throw new Error(`${label} state account cannot be executable`);
  }
  if (account.data[1] !== 'base64') {
    throw new Error(`${label} account encoding mismatch`);
  }

  const bytes = decodeBase64(account.data[0]);
  if (bytes.length !== profile.accountSize) {
    throw new Error(`${label} account size mismatch`);
  }
  if (
    profile.discriminatorHex &&
    bytesToHex(bytes.slice(0, profile.discriminatorHex.length / 2)) !== profile.discriminatorHex
  ) {
    throw new Error(`${label} discriminator mismatch`);
  }

  const decoded = {
    accountSize: bytes.length,
    mintA: readPublicKey(bytes, profile.layout.mintA),
    mintB: readPublicKey(bytes, profile.layout.mintB),
    owner: account.owner,
    vaultA: readPublicKey(bytes, profile.layout.vaultA),
    vaultB: readPublicKey(bytes, profile.layout.vaultB),
  };

  if (decoded.mintA !== profile.mintA.address || decoded.mintB !== profile.mintB.address) {
    throw new Error(`${label} mint orientation mismatch`);
  }
  if (decoded.vaultA !== profile.vaultA || decoded.vaultB !== profile.vaultB) {
    throw new Error(`${label} ${vaultNoun} mismatch`);
  }

  return decoded;
};

export const requestSolanaRpc = async <T>(
  id: string,
  method: string,
  params: unknown[],
): Promise<T> => {
  const response = await proxyAxios.post<SolanaRpcResponse<T>>('/api/v1/proxy', {
    body: JSON.stringify({
      id,
      jsonrpc: '2.0',
      method,
      params,
    }),
    url: SOLANA_RPC_URL,
  });

  const payload = response.data;
  if (payload.error) {
    throw new Error(`Solana RPC ${method} failed: ${payload.error.message}`);
  }
  if (!payload.result) {
    throw new Error(`Solana RPC ${method} returned no result`);
  }

  return payload.result;
};

export const decodeSolanaTokenAccount = (
  address: string,
  expectedMint: SolanaMint,
  account: SolanaAccount | null,
  label: string,
): SolanaTokenAccountSnapshot => {
  if (!account) {
    throw new Error(`${label} account is missing`);
  }
  if (!TOKEN_PROGRAM_IDS.has(account.owner)) {
    throw new Error(`${label} token program mismatch`);
  }
  if (account.executable || account.data[1] !== 'base64') {
    throw new Error(`${label} account shape mismatch`);
  }

  const bytes = decodeBase64(account.data[0]);
  if (bytes.length < 72) {
    throw new Error(`${label} account data is too short`);
  }

  const mint = readPublicKey(bytes, 0);
  if (mint !== expectedMint.address) {
    throw new Error(`${label} mint mismatch`);
  }

  const amountRaw = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    .getBigUint64(64, true)
    .toString();

  return {
    address,
    amountRaw,
    decimals: expectedMint.decimals,
    mint,
  };
};

export const decodeSolanaMintAccount = (
  expectedMint: SolanaMint,
  account: SolanaAccount | null,
  label: string,
): SolanaMintAccountSnapshot => {
  if (!account) {
    throw new Error(`${label} mint account is missing`);
  }
  if (!TOKEN_PROGRAM_IDS.has(account.owner)) {
    throw new Error(`${label} mint program mismatch`);
  }
  if (account.executable || account.data[1] !== 'base64') {
    throw new Error(`${label} mint account shape mismatch`);
  }

  const bytes = decodeBase64(account.data[0]);
  if (bytes.length < 46) {
    throw new Error(`${label} mint account data is too short`);
  }
  if (bytes[44] !== expectedMint.decimals) {
    throw new Error(`${label} mint decimals mismatch`);
  }
  if (bytes[45] !== 1) {
    throw new Error(`${label} mint is not initialized`);
  }

  return {
    address: expectedMint.address,
    decimals: bytes[44],
    program: account.owner,
  };
};

export const normalizeArrayOhlcv = (rows: unknown[][]): KLine[] => {
  const candles = new Map<number, KLine>();
  rows.forEach(row => {
    if (row.length < 6) return;
    const values = row.slice(0, 6).map(Number);
    if (values.some(value => !Number.isFinite(value))) return;

    const timestamp = values[0] * 1000;
    candles.set(timestamp, [
      timestamp,
      values[1].toString(),
      values[2].toString(),
      values[3].toString(),
      values[4].toString(),
      values[5].toString(),
    ]);
  });

  return Array.from(candles.values()).sort((a, b) => a[0] - b[0]);
};
